import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { Button, IconButton } from '../ui';
import {
    isPushSupported,
    getExistingSubscription,
    subscribeToPush,
} from '../../lib/pwa';

// PR 4b — opt-in banner shown inside the crew portal once a lead has
// PIN-authenticated. The banner only renders when push is supported,
// the VAPID public key is available, the lead doesn't already have a
// subscription, and the browser permission isn't already 'denied'.
//
// Flow:
//   1. user taps "Enable notifications"
//   2. browser prompts for permission
//   3. on grant → call subscribeToPush() → POST the result to
//      /api/crew-portal-push (action=subscribe)
//   4. banner dismisses itself; subsequent renders won't show it
//      because getExistingSubscription() now returns a value.
//
// Dismiss-X path: the user can tap × to hide for the session. We don't
// persist the dismissal — banner returns next visit. That's intentional;
// SMS is the primary notification channel, push is opt-in but we'd
// rather nudge them again than disappear forever.

export default function PushOptInBanner({ vapidPublicKey, token, onSubscribed, onError }) {
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const { show } = useToast();

    useEffect(() => {
        let cancelled = false;
        if (!isPushSupported() || !vapidPublicKey) return undefined;
        if (typeof Notification === 'undefined' || Notification.permission === 'denied') return undefined;

        (async () => {
            try {
                const existing = await getExistingSubscription();
                if (cancelled) return;
                if (!existing) setVisible(true);
            } catch (_) {
                // If the SW or pushManager isn't ready, just don't show
                // the banner. We can try again on next mount.
            }
        })();

        return () => { cancelled = true; };
    }, [vapidPublicKey]);

    if (!visible) return null;

    const handleEnable = async () => {
        setBusy(true);
        try {
            const sub = await subscribeToPush(vapidPublicKey);
            const res = await fetch('/api/crew-portal-push', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'subscribe',
                    token,
                    subscription: sub.toJSON(),
                }),
            });
            if (!res.ok) {
                let msg = `Server returned ${res.status}`;
                try { const body = await res.json(); if (body?.error) msg = body.error; } catch {}
                throw new Error(msg);
            }
            setVisible(false);
            if (onSubscribed) onSubscribed();
        } catch (err) {
            // Surface to both the user and the console. The previous
            // behavior swallowed errors to console.warn, which masked a
            // silent 500 from /api/crew-portal-push during the iOS PWA
            // rollout — banner appeared to "work" but nothing landed in
            // crew_push_subscriptions. Loud failure is better.
            console.warn('[push opt-in] failed:', err);
            const msg = (err && err.message) ? err.message : 'Could not enable notifications';
            try {
                show("Couldn't enable notifications", 'error', {
                    description: msg.length > 200 ? msg.slice(0, 200) + '…' : msg,
                });
            } catch (_) { /* Sonner not mounted — fall through */ }
            if (onError) onError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="p-3 rounded-xl border border-red-200 bg-red-50 flex items-start gap-3">
            <div className="text-2xl leading-none" aria-hidden="true">🔔</div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900">Get job updates on this device</div>
                <div className="text-xs text-slate-600 mt-0.5">
                    We'll ping you when dispatch changes a job — crew, truck, time, status. Texts still go out as backup.
                </div>
                <div className="mt-2 flex gap-2">
                    <Button onClick={handleEnable} loading={busy} variant="primary">
                        Enable notifications
                    </Button>
                    <Button onClick={() => setVisible(false)} variant="tertiary" disabled={busy}>
                        Not now
                    </Button>
                </div>
            </div>
            <IconButton
                size="sm"
                label="Dismiss"
                onClick={() => setVisible(false)}
            >
                ×
            </IconButton>
        </div>
    );
}
